/**
 * A finding, before it is put into words.
 *
 * The builders in `market-insights.ts` and `sales-insights.ts` return these —
 * a translation key and the raw numbers behind it — and the page puts them
 * into the reader's language. Keeping the words out of the arithmetic is
 * what lets the arithmetic be tested, and every language read the same thing.
 */

export type SpecTone = 'good' | 'bad' | 'watch' | 'neutral';

/** A value in a sentence; the renderer formats each kind for the active language. */
export type Param =
    | string
    | number
    | { money: number; currency?: string; digits?: number }
    /** Already in percent units: 12.5 reads "13%". */
    | { pct: number }
    /** 'YYYY-MM'. */
    | { month: string }
    | { date: string }
    /** Another translation, e.g. a check's name inside a sentence. */
    | { tkey: string; fallback?: string }
    /** Several translations, joined as a list ("phone, website and hours"). */
    | { list: string[] };

export interface Msg {
    key: string;
    values?: Record<string, Param>;
    /** Picks the plural form; show the number itself through a value, formatted. */
    count?: number;
}

export interface InsightSpec {
    id: string;
    tone: SpecTone;
    title: Msg;
    detail?: Msg;
    action?: { label: Msg; href: string };
}

/** A key under the BUSINESS namespace. */
export const msg = (key: string, values?: Record<string, Param>, count?: number): Msg => ({
    key: key.startsWith('BUSINESS.') ? key : `BUSINESS.${key}`,
    values,
    count,
});

export const money = (v: number, currency = 'usd', digits = 0): Param => ({ money: v, currency, digits });
export const pct = (fraction01: number): Param => ({ pct: fraction01 * 100 });
export const pctOf = (percent: number): Param => ({ pct: percent });

const TONE_ORDER: Record<SpecTone, number> = { bad: 0, watch: 1, good: 2, neutral: 3 };

/**
 * Most urgent first, keeping the builder's order within a tone — except the
 * `lead` ids (what changed), which always open the reading.
 */
export function prioritise(specs: InsightSpec[], limit: number, lead: string[] = []): InsightSpec[] {
    const first = specs.filter((s) => lead.includes(s.id));
    const rest = specs
        .filter((s) => !lead.includes(s.id))
        .map((s, i) => ({ s, i }))
        .sort((a, b) => TONE_ORDER[a.s.tone] - TONE_ORDER[b.s.tone] || a.i - b.i)
        .map((x) => x.s);
    return [...first, ...rest].slice(0, limit);
}

export const ROUTES = {
    overview: '/business/dashboard',
    search: '/business/visibility/search',
    profile: '/business/visibility/profile',
    backlinks: '/business/visibility/backlinks',
    health: '/business/visibility/health',
    rivals: '/business/competitors/rivals',
    gaps: '/business/competitors/gaps',
    revenue: '/business/sales/revenue',
    shop: '/business/sales/shop',
    catalog: '/products/catalog',
    analyse: '/home/dashboard',
} as const;

export const searchSegment = (segment: string) => `${ROUTES.search}?segment=${segment}`;
